// src/admin/components/tenants/TenantForm.jsx
import React, { useState, useEffect, useMemo } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RiUserLine, RiSaveLine, RiArrowLeftLine, RiErrorWarningLine,
  RiBuildingLine, RiMoneyDollarCircleLine, RiCheckboxCircleLine,
  RiSearchLine, RiArrowDownSLine, RiLayoutMasonryLine,
  RiCalendarLine, RiFileTextLine, RiCloseLine
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  getTenant, createTenant, updateTenant, validateTenantData,
} from '../../../models/tenantModel';
import { getAllApartments } from '../../../models/apartmentModel';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const TenantForm = ({ isEdit = false }) => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isRTL = ['ar', 'ku'].includes(i18n.language);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [availableApartments, setAvailableApartments] = useState([]);
  const [defaultCountry, setDefaultCountry] = useState('iq');
  const [apartmentInfo, setApartmentInfo] = useState(null);

  // Showroom picker state
  const [showroomSearch, setShowroomSearch] = useState('');
  const [showroomTypeFilter, setShowroomTypeFilter] = useState('all');
  const [expandedZones, setExpandedZones] = useState(new Set());

  const today = new Date().toISOString().split('T')[0];

  const formatDateForBackend = (date) => {
    if (!date) return '';
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) return date;
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch { return ''; }
  };

  const [form, setForm] = useState({
    name: '',
    phone: '',
    preferredLanguage: 'ku',
    apartmentId: '',
    rent: '',
    discount: '0',
    serviceFee: '0',
    officeCommission: '0',
    insurance: '0',
    contractStartDate: '',
    contractEndDate: '',
    paymentDate: '',
    paymentEndDate: '',
    notes: '',
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  // Load apartments on mount
  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then(d => { if (d.country_code) setDefaultCountry(d.country_code.toLowerCase()); })
      .catch(() => {});

    const load = async () => {
      try {
        const res = await getAllApartments();
        setAvailableApartments(res.apartments || []);
      } catch {
        setAvailableApartments([]);
      }
    };
    load();
  }, []);

  // Load tenant data if editing
  useEffect(() => {
    const loadTenant = async () => {
      if (isEdit && id) {
        try {
          setLoading(true);
          const response = await getTenant(id);
          if (response.tenant) {
            const tenant = response.tenant;
            setForm({
              name: tenant.name || '',
              phone: tenant.phone || '',
              preferredLanguage: tenant.preferredLanguage || 'ku',
              apartmentId: tenant.apartmentId || '',
              rent: tenant.rent || '',
              discount: tenant.discount || '0',
              serviceFee: tenant.serviceFee || '0',
              officeCommission: tenant.officeCommission || '0',
              insurance: tenant.insurance || '0',
              contractStartDate: formatDateForBackend(tenant.contractStartDate) || '',
              contractEndDate: formatDateForBackend(tenant.contractEndDate) || '',
              paymentDate: formatDateForBackend(tenant.paymentDate) || '',
              paymentEndDate: formatDateForBackend(tenant.paymentEndDate) || '',
              notes: tenant.notes || '',
            });
            if (tenant.apartmentNumber || tenant.buildingName) {
              setApartmentInfo({
                number: tenant.apartmentNumber,
                buildingName: tenant.buildingName || '',
                floor: tenant.floor || '',
              });
            }
          }
        } catch (err) {
          setError(t('failed_to_load_tenant_data'));
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    loadTenant();
  }, [id]);

  // Group apartments by floor (zone)
  const groupedByZone = useMemo(() => {
    const groups = {};
    availableApartments.forEach(apt => {
      const zone = apt.floor || 1;
      if (!groups[zone]) groups[zone] = [];
      groups[zone].push(apt);
    });
    Object.values(groups).forEach(arr =>
      arr.sort((a, b) => {
        const na = parseInt(a.apartmentNumber), nb = parseInt(b.apartmentNumber);
        return isNaN(na) || isNaN(nb) ? a.apartmentNumber.localeCompare(b.apartmentNumber) : na - nb;
      })
    );
    return Object.keys(groups)
      .sort((a, b) => Number(a) - Number(b))
      .map(zk => ({ zone: zk, units: groups[zk] }));
  }, [availableApartments]);

  const distinctTypes = useMemo(() => {
    const s = new Set(availableApartments.map(a => a.type).filter(Boolean));
    return [...s].sort();
  }, [availableApartments]);

  const filteredGroups = useMemo(() => {
    const q = showroomSearch.trim().toLowerCase();
    return groupedByZone.map(({ zone, units }) => ({
      zone,
      units: units.filter(apt => {
        const matchSearch = !q ||
          String(apt.apartmentNumber).toLowerCase().includes(q) ||
          (apt.buildingName || '').toLowerCase().includes(q) ||
          String(apt.floor || '').toLowerCase().includes(q) ||
          (apt.type || '').toLowerCase().includes(q);
        const matchType = showroomTypeFilter === 'all' || apt.type === showroomTypeFilter;
        return matchSearch && matchType;
      })
    })).filter(g => g.units.length > 0);
  }, [groupedByZone, showroomSearch, showroomTypeFilter]);

  // Auto-expand first zone
  useEffect(() => {
    if (groupedByZone.length > 0 && expandedZones.size === 0) {
      setExpandedZones(new Set([groupedByZone[0].zone]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedByZone]);

  const toggleZone = (zone) => {
    setExpandedZones(prev => {
      const n = new Set(prev);
      n.has(zone) ? n.delete(zone) : n.add(zone);
      return n;
    });
  };

  const selectedApt = availableApartments.find(a => a.id === form.apartmentId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validation = validateTenantData(form);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    try {
      setSaving(true);
      const backendData = {
        name: form.name.trim(),
        phone: form.phone,
        rent: parseFloat(form.rent) || 0,
        discount: parseFloat(form.discount) || 0,
        serviceFee: parseFloat(form.serviceFee) || 0,
        officeCommission: parseFloat(form.officeCommission) || 0,
        insurance: parseFloat(form.insurance) || 0,
        contractStartDate: formatDateForBackend(form.contractStartDate),
        contractEndDate: formatDateForBackend(form.contractEndDate),
        paymentDate: formatDateForBackend(form.paymentDate),
        apartmentId: form.apartmentId,
        preferredLanguage: form.preferredLanguage,
      };
      if (form.notes) backendData.notes = form.notes;
      if (isEdit && form.paymentEndDate) backendData.paymentEndDate = formatDateForBackend(form.paymentEndDate);

      if (isEdit) {
        await updateTenant(id, backendData);
      } else {
        await createTenant(backendData);
      }
      navigate('/admin/tenants');
    } catch (err) {
      setError(err.message || t('failed_to_save_tenant'));
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `w-full p-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-red-500
    ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-500' : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400'}`;
  const labelClass = `block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`;
  const sectionClass = `p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-zinc-750' : 'bg-zinc-50'}`;

  const errInput = (field) => validationErrors[field]
    ? `w-full p-2.5 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-red-500 border-red-500 ${isDarkMode ? 'bg-red-900/20 text-white' : 'bg-red-50 text-zinc-900'}`
    : inputClass;

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <div className="animate-spin h-8 w-8 border-4 border-red-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className={`${isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'} rounded-xl border shadow-lg`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className={`p-5 border-b ${isDarkMode ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <RiUserLine className={`me-3 text-2xl ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
            <div>
              <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                {isEdit ? t('edit_tenant') : t('add_tenant')}
              </h1>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                {isEdit ? t('edit_tenant_subtitle', 'Update renter details') : t('add_tenant_subtitle', 'Add a new renter to a showroom unit')}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/tenants')}
            className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-zinc-100 hover:bg-zinc-200'} text-zinc-600 transition-colors`}
          >
            <RiArrowLeftLine size={20} className="rtl:rotate-180" />
          </button>
        </div>
      </div>

      {error && (
        <div className={`mx-5 mt-5 p-4 rounded-lg flex items-center gap-3 ${isDarkMode ? 'bg-red-900/20 border border-red-800 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          <RiErrorWarningLine size={18} className="flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-5">

        {/* ── Section 1: Renter Info ── */}
        <div className={sectionClass}>
          <h2 className={`text-base font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
            <RiUserLine className="text-red-500" />
            {t('renter_information', 'Renter Information')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t('full_name', 'Full Name')} <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.name}
                onChange={e => { set('name', e.target.value); if (validationErrors.name) setValidationErrors(prev => ({ ...prev, name: undefined })); }}
                placeholder={t('full_name_placeholder', 'Enter renter full name')}
                className={errInput('name')}
                required
              />
              {validationErrors.name && <p className={`mt-1 text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{validationErrors.name}</p>}
            </div>

            <div>
              <label className={labelClass}>{t('phone')} <span className="text-red-500">*</span></label>
              <div dir="ltr">
                <PhoneInput
                  country={defaultCountry}
                  value={form.phone}
                  onChange={phone => { set('phone', '+' + phone); if (validationErrors.phone) setValidationErrors(prev => ({ ...prev, phone: undefined })); }}
                  enableSearch
                  disableSearchIcon
                  buttonStyle={{ backgroundColor: isDarkMode ? '#3f3f46' : '#f9fafb', borderColor: isDarkMode ? '#52525b' : '#d1d5db' }}
                  inputStyle={{ width: '100%', backgroundColor: isDarkMode ? '#3f3f46' : '#ffffff', borderColor: validationErrors.phone ? '#ef4444' : (isDarkMode ? '#52525b' : '#d1d5db'), color: isDarkMode ? '#fff' : '#111827' }}
                  dropdownStyle={{ backgroundColor: isDarkMode ? '#27272a' : '#ffffff', color: isDarkMode ? '#fff' : '#111827' }}
                />
              </div>
              {validationErrors.phone && <p className={`mt-1 text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{validationErrors.phone}</p>}
            </div>

            <div>
              <label className={labelClass}>{t('preferred_language')}</label>
              <select value={form.preferredLanguage} onChange={e => set('preferredLanguage', e.target.value)} className={inputClass}>
                <option value="ku">کوردی</option>
                <option value="en">English</option>
                <option value="ar">العربية</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>{t('notes_optional', 'Notes (optional)')}</label>
              <input
                type="text"
                value={form.notes}
                onChange={e => set('notes', e.target.value)}
                placeholder={t('notes_placeholder', 'Any additional notes...')}
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* ── Section 2: Select Showroom Unit ── */}
        <div className={sectionClass}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-base font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              <RiBuildingLine className="text-red-500" />
              {t('select_showroom_unit', 'Select Showroom Unit')} <span className="text-red-500">*</span>
            </h2>
            {!isEdit && (
              <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
                {availableApartments.filter(a => a.status === 'vacant').length} {t('vacant', 'vacant')}
              </span>
            )}
          </div>

          {isEdit ? (
            <div className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-zinc-400' : 'bg-zinc-100 border-zinc-300 text-zinc-500'} cursor-not-allowed text-sm`}>
              {apartmentInfo
                ? `${apartmentInfo.buildingName ? apartmentInfo.buildingName + ' — ' : ''}${t('unit', 'Unit')} ${apartmentInfo.number}${apartmentInfo.floor ? ` · Zone ${apartmentInfo.floor}` : ''}`
                : (form.apartmentId || t('no_apartment', 'No unit assigned'))}
            </div>
          ) : availableApartments.length === 0 ? (
            <div className={`py-8 text-center rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
              <RiBuildingLine size={32} className="mx-auto mb-2 text-zinc-400" />
              <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('no_apartments_available', 'No units available')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Selected unit badge */}
              {selectedApt && (
                <div className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-red-900/20 border-red-700/50' : 'bg-red-50 border-red-200'}`}>
                  <RiCheckboxCircleLine className="text-red-500 shrink-0" size={16} />
                  <span className={`text-sm font-semibold ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>#{selectedApt.apartmentNumber}</span>
                  <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                    · {selectedApt.buildingName} · Zone {selectedApt.floor} · {selectedApt.type} · {selectedApt.size} m²
                  </span>
                  <button type="button" onClick={() => set('apartmentId', '')}
                    className={`ms-auto text-xs px-2 py-0.5 rounded ${isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-800'}`}>
                    {t('change', 'Change')}
                  </button>
                </div>
              )}

              {/* Search + type filter */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <RiSearchLine size={15} className={`absolute top-1/2 -translate-y-1/2 start-3 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`} />
                  <input
                    type="text"
                    value={showroomSearch}
                    onChange={e => setShowroomSearch(e.target.value)}
                    placeholder={t('search_showroom_placeholder', 'Search by unit #, zone, type…')}
                    className={`w-full ps-9 pe-3 py-2 text-sm rounded-lg border outline-none focus:ring-2 focus:ring-red-500/40 ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-500' : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400'}`}
                  />
                </div>
                <select
                  value={showroomTypeFilter}
                  onChange={e => setShowroomTypeFilter(e.target.value)}
                  className={`text-sm px-3 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-red-500/40 ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`}
                >
                  <option value="all">{t('all_types', 'All Types')}</option>
                  {distinctTypes.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                </select>
              </div>

              {/* Zone accordion */}
              {filteredGroups.length === 0 ? (
                <div className={`py-6 text-center rounded-lg border-2 border-dashed ${isDarkMode ? 'border-zinc-700 text-zinc-500' : 'border-zinc-200 text-zinc-400'}`}>
                  <p className="text-sm">{t('no_showrooms_match_search', 'No units match your search.')}</p>
                  <button type="button" onClick={() => { setShowroomSearch(''); setShowroomTypeFilter('all'); }}
                    className="mt-2 text-xs text-red-500 hover:underline">
                    {t('clear_filters', 'Clear filters')}
                  </button>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pe-1">
                  {filteredGroups.map(({ zone, units }) => {
                    const isExpanded = expandedZones.has(zone);
                    const vacantCt = units.filter(a => a.status === 'vacant').length;
                    return (
                      <div key={zone} className={`rounded-xl border overflow-hidden transition-shadow ${isDarkMode ? 'bg-zinc-800/60 border-zinc-700' : 'bg-white border-zinc-200'} ${isExpanded ? 'shadow-md' : 'shadow-sm hover:shadow-md'}`}>
                        <button
                          type="button"
                          onClick={() => toggleZone(zone)}
                          className={`w-full text-start px-4 py-3 flex items-center justify-between transition-colors ${isDarkMode ? 'hover:bg-zinc-700/50' : 'hover:bg-zinc-50'} ${isExpanded ? `border-b ${isDarkMode ? 'border-zinc-700' : 'border-zinc-100'}` : ''}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-bold text-xs shrink-0 ${isDarkMode ? 'bg-zinc-700 border border-zinc-600 text-zinc-300' : 'bg-zinc-100 border border-zinc-200 text-zinc-700'}`}>
                              <RiLayoutMasonryLine className={isDarkMode ? 'text-red-400' : 'text-red-500'} size={13} />
                              {t('zone', 'ZONE')} {zone}
                            </div>
                            <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{units.length} {t('units', 'units')}</span>
                            {vacantCt > 0 && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-500/10 text-green-400 border border-green-500/20">
                                {vacantCt} Vacant
                              </span>
                            )}
                          </div>
                          <RiArrowDownSLine size={18} className={`transition-transform duration-250 ${isExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`} />
                        </button>

                        {isExpanded && (() => {
                          const sorted = [...units].sort((a, b) => {
                            const na = parseInt(a.apartmentNumber), nb = parseInt(b.apartmentNumber);
                            return isNaN(na) || isNaN(nb) ? a.apartmentNumber.localeCompare(b.apartmentNumber) : na - nb;
                          });
                          const hasPosData = sorted.some(a => a.rowPosition);
                          let frontRow, backRow, cornerUnits;
                          if (hasPosData) {
                            frontRow    = sorted.filter(a => a.rowPosition === 'front');
                            backRow     = [...sorted.filter(a => a.rowPosition === 'back')].reverse();
                            cornerUnits = sorted.filter(a => a.rowPosition === 'corner');
                          } else {
                            const half = Math.floor(sorted.length / 2);
                            frontRow    = sorted.slice(0, half);
                            backRow     = [...sorted.slice(half)].reverse();
                            cornerUnits = [];
                          }

                          const UnitCard = ({ apt }) => {
                            const selected = form.apartmentId === apt.id;
                            const isOccupied = apt.status !== 'vacant';
                            const statusColor = isOccupied ? '#f59e0b' : '#3b82f6';
                            return (
                              <div
                                onClick={() => !isOccupied && set('apartmentId', apt.id)}
                                className={`relative flex flex-col rounded-xl border-2 overflow-hidden select-none transition-all shrink-0 w-[110px]
                                  ${isOccupied ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:-translate-y-1'}
                                  ${selected
                                    ? isDarkMode ? 'border-red-500 bg-red-900/20 shadow-lg shadow-red-900/30' : 'border-red-500 bg-red-50'
                                    : isDarkMode ? 'border-zinc-700 bg-zinc-800 hover:border-zinc-500 hover:shadow-lg' : 'border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-md'
                                  }`}
                              >
                                <div className="absolute inset-x-0 top-0 h-1 rounded-t-xl"
                                  style={{ backgroundColor: selected ? '#ef4444' : statusColor }} />
                                <div className="p-2.5 pt-3.5 flex flex-col gap-1">
                                  <div className="flex items-start justify-between">
                                    <span className={`font-mono font-black text-lg leading-none ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                      {apt.apartmentNumber}
                                    </span>
                                    {selected
                                      ? <RiCheckboxCircleLine className="text-red-500 shrink-0" size={15} />
                                      : <span className="text-[8px] font-bold uppercase px-1 py-0.5 rounded"
                                          style={{ backgroundColor: `${statusColor}20`, color: statusColor, border: `1px solid ${statusColor}30` }}>
                                          {t(apt.status, apt.status)}
                                        </span>
                                    }
                                  </div>
                                  {apt.type && (
                                    <span className={`self-start text-[8px] font-bold uppercase px-1.5 py-0.5 rounded border ${isDarkMode ? 'bg-blue-500/20 text-blue-300 border-blue-500/40' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>
                                      {apt.type}
                                    </span>
                                  )}
                                  {apt.size && (
                                    <p className={`text-[9px] mt-0.5 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{apt.size} m²</p>
                                  )}
                                </div>
                              </div>
                            );
                          };

                          return (
                            <div className={`p-3 ${isDarkMode ? 'bg-zinc-900/30' : 'bg-zinc-50/50'}`}>
                              <div className="overflow-x-auto">
                                <div className={`inline-flex gap-3 p-3 rounded-2xl border min-w-max ${isDarkMode ? 'bg-zinc-900/60 border-zinc-700/60' : 'bg-zinc-50/80 border-zinc-200'}`}>
                                  <div className="flex flex-col gap-2">
                                    {/* Back row */}
                                    <div className="flex flex-col gap-1.5">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-zinc-200 text-zinc-500'}`}>
                                          ↑ {t('back_row', 'Back Row')}
                                        </span>
                                      </div>
                                      <div className={`flex gap-2 px-3 py-2 rounded-xl border border-dashed ${isDarkMode ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-300 bg-white/60'}`}>
                                        {backRow.map(apt => <UnitCard key={apt.id} apt={apt} />)}
                                      </div>
                                    </div>
                                    {/* Wall */}
                                    <div className="flex items-center gap-2 px-2">
                                      <div className={`flex-1 h-2 rounded-full ${isDarkMode ? 'bg-zinc-600' : 'bg-zinc-300'}`} />
                                      <span className={`text-[9px] font-bold uppercase tracking-widest shrink-0 ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>─ {t('wall', 'wall')} ─</span>
                                      <div className={`flex-1 h-2 rounded-full ${isDarkMode ? 'bg-zinc-600' : 'bg-zinc-300'}`} />
                                    </div>
                                    {/* Front row */}
                                    <div className="flex flex-col gap-1.5">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-zinc-200 text-zinc-500'}`}>
                                          ↓ {t('front_row', 'Front Row')}
                                        </span>
                                      </div>
                                      <div className={`flex gap-2 px-3 py-2 rounded-xl border border-dashed ${isDarkMode ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-300 bg-white/60'}`}>
                                        {frontRow.map(apt => <UnitCard key={apt.id} apt={apt} />)}
                                      </div>
                                    </div>
                                  </div>
                                  {cornerUnits.length > 0 && (
                                    <div className={`flex flex-col gap-2 justify-center ps-3 border-s ${isDarkMode ? 'border-zinc-700' : 'border-zinc-300'}`}>
                                      <span className={`text-[10px] font-bold uppercase tracking-widest text-center ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('corner', 'Corner')}</span>
                                      {[...cornerUnits].reverse().map(apt => <UnitCard key={apt.id} apt={apt} />)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              )}
              {validationErrors.apartmentId && (
                <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{validationErrors.apartmentId}</p>
              )}
            </div>
          )}
        </div>

        {/* ── Section 3: Contract Details ── */}
        <div className={sectionClass}>
          <h2 className={`text-base font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
            <RiMoneyDollarCircleLine className="text-red-500" />
            {t('contract_information', 'Contract Details')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div>
              <label className={labelClass}>{t('base_rate', 'Base Rate')} <span className="text-red-500">*</span></label>
              <input type="number" min="0" value={form.rent}
                onChange={e => { set('rent', e.target.value); if (validationErrors.rent) setValidationErrors(prev => ({ ...prev, rent: undefined })); }}
                className={errInput('rent')} required={!isEdit} />
              {validationErrors.rent && <p className={`mt-1 text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{validationErrors.rent}</p>}
            </div>

            <div>
              <label className={labelClass}>{t('discount', 'Discount')}</label>
              <input type="number" min="0" value={form.discount} onChange={e => set('discount', e.target.value)} className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>{t('service_fee', 'Service Fee')}</label>
              <input type="number" min="0" value={form.serviceFee} onChange={e => set('serviceFee', e.target.value)} className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>{t('office_commission', 'Office Commission')}</label>
              <input type="number" min="0" value={form.officeCommission} onChange={e => set('officeCommission', e.target.value)} className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>{t('insurance', 'Insurance')}</label>
              <input type="number" min="0" value={form.insurance} onChange={e => set('insurance', e.target.value)} className={inputClass} />
            </div>

            {/* Contract Start */}
            <div>
              <label className={labelClass}>{t('contract_start_date')} <span className="text-red-500">*</span></label>
              <div className="relative">
                <DatePicker
                  selected={form.contractStartDate ? new Date(form.contractStartDate) : null}
                  onChange={date => { set('contractStartDate', formatDateForBackend(date)); if (validationErrors.contractStartDate) setValidationErrors(prev => ({ ...prev, contractStartDate: undefined })); }}
                  dateFormat="MM/dd/yyyy" placeholderText="mm/dd/yyyy"
                  showMonthDropdown showYearDropdown dropdownMode="select" isClearable portalId="root-portal"
                  className={errInput('contractStartDate')} required={!isEdit}
                />
                <RiCalendarLine className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none`} />
              </div>
              {validationErrors.contractStartDate && <p className={`mt-1 text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{validationErrors.contractStartDate}</p>}
            </div>

            {/* Contract End */}
            <div>
              <label className={labelClass}>{t('contract_end_date')} <span className="text-red-500">*</span></label>
              <div className="relative">
                <DatePicker
                  selected={form.contractEndDate ? new Date(form.contractEndDate) : null}
                  onChange={date => { set('contractEndDate', formatDateForBackend(date)); if (validationErrors.contractEndDate) setValidationErrors(prev => ({ ...prev, contractEndDate: undefined })); }}
                  dateFormat="MM/dd/yyyy" placeholderText="mm/dd/yyyy"
                  showMonthDropdown showYearDropdown dropdownMode="select" isClearable portalId="root-portal"
                  className={errInput('contractEndDate')} required={!isEdit}
                />
                <RiCalendarLine className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none`} />
              </div>
              {validationErrors.contractEndDate && <p className={`mt-1 text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{validationErrors.contractEndDate}</p>}
            </div>

            {/* Payment Date */}
            <div>
              <label className={labelClass}>{t('payment_date')} <span className="text-red-500">*</span></label>
              <div className="relative">
                <DatePicker
                  selected={form.paymentDate ? new Date(form.paymentDate) : null}
                  onChange={date => { set('paymentDate', formatDateForBackend(date)); if (validationErrors.paymentDate) setValidationErrors(prev => ({ ...prev, paymentDate: undefined })); }}
                  dateFormat="MM/dd/yyyy" placeholderText="mm/dd/yyyy"
                  showMonthDropdown showYearDropdown dropdownMode="select" isClearable portalId="root-portal"
                  className={errInput('paymentDate')} required={!isEdit}
                />
                <RiCalendarLine className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none`} />
              </div>
              {validationErrors.paymentDate && <p className={`mt-1 text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{validationErrors.paymentDate}</p>}
            </div>

            {/* Payment End Date — edit mode only */}
            {isEdit && (
              <div>
                <label className={labelClass}>{t('payment_end_date', 'Payment End Date')}</label>
                <div className="relative">
                  <DatePicker
                    selected={form.paymentEndDate ? new Date(form.paymentEndDate) : null}
                    onChange={date => set('paymentEndDate', formatDateForBackend(date))}
                    dateFormat="MM/dd/yyyy" placeholderText="mm/dd/yyyy"
                    showMonthDropdown showYearDropdown dropdownMode="select" isClearable portalId="root-portal"
                    className={inputClass}
                  />
                  <RiCalendarLine className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none`} />
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-2">
          <button
            type="button"
            onClick={() => navigate('/admin/tenants')}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-zinc-300' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700'}`}
            disabled={saving}
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {saving ? (
              <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {t('saving')}</>
            ) : (
              <><RiSaveLine size={18} /> {isEdit ? t('update_tenant') : t('create_tenant')}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TenantForm;
